import ReportVisitor from "../reports/ReportVisitor.js";

/**
 * This class is in charge to know if a row/column is visible or not.
 *
 * It keeps this in a cache for speed issues, as a usual way to know if a row is visible is
 * to ask its parent, which in turn, will ask all its children to know if one has a value.
 *
 * It implements a visitor to easily centralize all the `isVisible` logics.
 * DO NOT CALL ACCEPT() DIRECTLY, or it will bypass the cache mechanism.
 */
export default class VisibilityCache extends ReportVisitor {
	constructor() {
		super(...arguments);
		this._cache = {};
	}

	flush() {
		this._cache = {};
	}

	invalidate({ item }) {
		delete this._cache[this._getItemId(item)];
	}

	// for the sake of double dispatch API completion
	visit(item) {
		return this.isVisible(item);
	}

	hasVisibleChildren({ row }) {
		return row.getRows().some((each) => this.isVisible(each));
	}

	getVisibleChildren({ row }) {
		return row.getRows().filter((each) => this.isVisible(each));
	}

	isVisible(item) {
		const cachedValue = this._getItemVisibility(item);
		if (cachedValue !== undefined) {
			return cachedValue;
		}

		const visible = item.accept(this);
		this._storeItemVisibility({ item, visible });
		return visible;
	}

	visitGeneratedAccountGroupRow(row) {
		return !row.isExcluded() && this.visitAccountGroupRow(row);
	}

	visitAbstractValueRow(row) {
		if (!this.visitAbstractRow(row)) {
			return false;
		}

		if (row.isVisibleWhenNoData()) {
			return true;
		}

		return row.getReport().isDisplaySettingsShowingRow(row);
	}

	visitAbstractRow(row) {
		if (!this.visitAbstractItem(row)) {
			return false;
		}

		if (!this.isVisible(row.getParent())) {
			return false;
		}

		const masterVisibilityRow = row.getMasterVisibilityRow();

		if (!masterVisibilityRow) {
			return true;
		}

		return this.isVisible(masterVisibilityRow);
	}

	visitAbstractItem(item) {
		return !item.getHidden();
	}

	visitRepeatingRow() {
		return true;
	}

	visitReport() {
		return true;
	}

	visitAbstractColumn(column) {
		if (column.getHidden()) {
			return false;
		}

		return column.shouldShowContent();
	}

	visitAbstractPeriodColumn(column) {
		if (column.isOptional()) return !column.getHidden();
		if (column.getHidden()) return false;
		if (!column.shouldShowContent()) return false;

		return !column.getPeriod().isNullPeriod();
	}

	visitAbstractForecastColumn() {
		return true;
	}

	visitDeviationColumn(column) {
		const baseValuesColumn = column.getBaseValuesColumn();
		const deviatedValuesColumn = column.getDeviatedValuesColumn();

		return (
			this.visitAbstractColumn(column) &&
			baseValuesColumn &&
			baseValuesColumn.hasData() &&
			deviatedValuesColumn &&
			deviatedValuesColumn.hasData()
		);
	}

	visitPercentageColumn(column) {
		return (
			this.visitAbstractColumn(column) &&
			column.getRow()?.hasData() &&
			column.getColumn()?.hasData()
		);
	}

	visitGroupColumn(column) {
		if (column.isOptional()) return !column.getHidden();
		if (column.getHidden()) return false;

		return column.shouldShowContent();
	}

	_getItemId(item) {
		return item.getId();
	}

	_getItemVisibility(item) {
		return this._cache[this._getItemId(item)];
	}

	_storeItemVisibility({ item, visible }) {
		this._cache[this._getItemId(item)] = visible;
	}
}
